/**
 * useChartType — replaces `useIsDentist` as the single source of truth
 * for specialty-aware chart gating. Looks up the viewer's
 * specialization (from /api/organisation/member/tour-status) and
 * returns the matching chart plugin's specialty token, or `null` if no
 * plugin matches.
 *
 * MVP returns one of: `'dental'` | `null`. Future specialties extend
 * this set by registering their plugin in `chartPluginRegistry`.
 *
 * Module-level cache mirrors the previous `useIsDentist` behaviour
 * (one fetch per session, fail-closed to `null`).
 */

import { useEffect, useState } from 'react';

import { fetchTourStatus } from '../../services/api/prescriptionTourApi';
import dentalPlugin from '../plugins/dental';

// MVP plugin list. PR-graduates to a registry when plugin #2 lands.
const PLUGINS = [dentalPlugin];

let cached = null;          // string | null — resolved specialty token
let resolved = false;       // distinguishes "not yet fetched" from "fetched, no match"
let inFlight = null;        // dedup concurrent calls

async function resolveChartType() {
  if (resolved) return cached;
  if (inFlight) return inFlight;
  inFlight = fetchTourStatus()
    .then((data) => {
      const raw = data?.specialization;
      const match = PLUGINS.find((p) => {
        try { return p.matchSpecialization(raw); }
        catch (_e) { return false; }
      });
      cached = match ? match.specialty : null;
      resolved = true;
      return cached;
    })
    .catch(() => {
      // Treat unknown specialty as no plugin — fail-closed so nothing
      // flashes for users on an unrecognised specialization.
      cached = null;
      resolved = true;
      return null;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * @returns {string|null} — chart specialty token (`'dental'` today) or
 *   `null` while resolving / when no plugin matches.
 */
export default function useChartType() {
  const [value, setValue] = useState(resolved ? cached : null);

  useEffect(() => {
    if (resolved) {
      if (value !== cached) setValue(cached);
      return undefined;
    }
    let alive = true;
    resolveChartType().then((v) => { if (alive) setValue(v); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return value;
}

/**
 * Convenience predicate. `useIsChartType('dental')` is the
 * direct migration target for `useIsDentist()`.
 */
export function useIsChartType(type) {
  return useChartType() === type;
}

/**
 * @returns {boolean} — whether specialty resolution has completed. Lets a
 *   no-chart fallback (e.g. the generic Procedure Summary) wait until we KNOW
 *   there's no plugin, instead of flashing while `useChartType()` is still
 *   resolving (it returns `null` both during resolution AND on no-match).
 */
export function useChartTypeReady() {
  const [ready, setReady] = useState(resolved);
  useEffect(() => {
    if (resolved) {
      if (!ready) setReady(true);
      return undefined;
    }
    let alive = true;
    resolveChartType().then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ready;
}

// Reset the resolved-specialty cache. Called on logout so the next
// account re-resolves its OWN specialty instead of inheriting the
// previous user's value (the cache is module-level and otherwise only
// clears on a full page reload). Also used to reset state between tests.
export function resetChartTypeCache() {
  cached = null;
  resolved = false;
  inFlight = null;
}
